// ============================================
// pages/Report/SalonExpenseReport.js
// Salon Expense Report — Totals, Avg, Category Breakdown, Recent Transactions
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';
import {
  Wallet, TrendingDown, BarChart3, Calendar,
  X, Loader2, Tag, ArrowDownCircle,
} from 'lucide-react';

// ─── Helpers ───────────────────────────────────────────────────────────────
function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function toISO(dateStr, endOfDay = false) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  else          d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

const FILTERS = [
  { id: 'today',   label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week',    label: 'This Week' },
  { id: 'month',   label: 'This Month' },
  { id: '3months', label: 'Last 3 Months' },
  { id: 'custom',  label: 'Custom' },
];

function getRange(filterId) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  switch (filterId) {
    case 'today':
      return { start: new Date(y, m, d, 0, 0, 0), end: new Date(y, m, d, 23, 59, 59, 999) };
    case 'yesterday':
      return { start: new Date(y, m, d - 1, 0, 0, 0), end: new Date(y, m, d - 1, 23, 59, 59, 999) };
    case 'week': {
      const day = now.getDay();
      return { start: new Date(y, m, d - (day === 0 ? 6 : day - 1), 0, 0, 0), end: new Date(y, m, d, 23, 59, 59, 999) };
    }
    case 'month':
      return { start: new Date(y, m, 1, 0, 0, 0), end: new Date(y, m, d, 23, 59, 59, 999) };
    case '3months':
      return { start: new Date(y, m - 3, d, 0, 0, 0), end: new Date(y, m, d, 23, 59, 59, 999) };
    default: return null;
  }
}

function rangeLabel(filterId, custom) {
  if (filterId === 'custom' && custom.start && custom.end)
    return `${fmtDate(custom.start)} → ${fmtDate(custom.end)}`;
  const r = getRange(filterId);
  return r ? `${fmtDate(r.start)} → ${fmtDate(r.end)}` : '';
}

const CAT_COLORS = {
  Rent:        'bg-blue-500',    Salary:      'bg-green-500',
  Utilities:   'bg-yellow-500',  Products:    'bg-pink-500',
  Equipment:   'bg-orange-500',  Marketing:   'bg-purple-500',
  Maintenance: 'bg-cyan-500',    Food:        'bg-lime-500',
  Transport:   'bg-indigo-500',  Other:       'bg-gray-400',
};
const CAT_LIGHT = {
  Rent:        'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  Salary:      'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  Utilities:   'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
  Products:    'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',
  Equipment:   'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
  Marketing:   'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
  Maintenance: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
  Food:        'bg-lime-50 dark:bg-lime-900/20 text-lime-700 dark:text-lime-300',
  Transport:   'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
  Other:       'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
};

// ─── Stat card ─────────────────────────────────────────────────────────────
function StatCard({ title, value, sub, gradient, icon: Icon }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl p-6 ${gradient} shadow-sm`}>
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10"/>
      <div className="absolute -right-1 -bottom-5 w-14 h-14 rounded-full bg-white/10"/>
      <div className="relative">
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3">
          <Icon size={20} className="text-white"/>
        </div>
        <p className="text-xs font-bold text-white/70 uppercase tracking-widest">{title}</p>
        <p className="text-2xl font-black text-white mt-1">{value}</p>
        {sub && <p className="text-xs text-white/60 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Custom date modal ─────────────────────────────────────────────────────
function CustomDateModal({ open, onClose, onApply }) {
  const [start, setStart] = useState('');
  const [end,   setEnd]   = useState('');
  const [err,   setErr]   = useState('');
  if (!open) return null;
  const maxDate = new Date().toISOString().split('T')[0];
  const apply = () => {
    if (!start || !end) { setErr('Select both dates'); return; }
    if (start > end)    { setErr('Start must be before end'); return; }
    onApply(start, end); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-black text-gray-900 dark:text-white">Custom Range</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400"><X size={16}/></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">From</label>
            <input type="date" max={maxDate} value={start} onChange={e => setStart(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800 dark:text-white"/>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">To</label>
            <input type="date" max={maxDate} min={start} value={end} onChange={e => setEnd(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800 dark:text-white"/>
          </div>
          {err && <p className="text-xs text-red-500 font-medium">{err}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-400">Cancel</button>
          <button onClick={apply} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold">Apply</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function SalonExpenseReport() {
  const { userToken: token } = useAuth();
  const [activeFilter, setActiveFilter] = useState('month');
  const [custom,       setCustom]       = useState({ start: '', end: '' });
  const [showCustom,   setShowCustom]   = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [expenses,     setExpenses]     = useState([]);

  // Computed
  const total   = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const count   = expenses.length;
  const avg     = count > 0 ? total / count : 0;
  const maxAmt  = Math.max(...expenses.map(e => parseFloat(e.amount || 0)), 1);

  // Category breakdown
  const catMap = {};
  expenses.forEach(e => {
    const c = e.category || 'Other';
    if (!catMap[c]) catMap[c] = { total: 0, count: 0 };
    catMap[c].total += parseFloat(e.amount || 0);
    catMap[c].count += 1;
  });
  const categories = Object.entries(catMap)
    .map(([cat, v]) => ({ cat, total: v.total, count: v.count }))
    .sort((a, b) => b.total - a.total);

  const recentExpenses = [...expenses]
    .sort((a, b) => new Date(b.expense_date || b.created_at) - new Date(a.expense_date || a.created_at))
    .slice(0, 15);

  const fetchExpenses = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      let start, end;
      if (activeFilter === 'custom') {
        if (!custom.start || !custom.end) { setLoading(false); return; }
        start = toISO(custom.start, false);
        end   = toISO(custom.end,   true);
      } else {
        const r = getRange(activeFilter);
        start = r.start.toISOString();
        end   = r.end.toISOString();
      }
      const res  = await fetch(
        `${SALON_API_URL}/expenses?startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}&limit=2000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) setExpenses(data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, activeFilter, custom]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleFilter = (id) => {
    if (id === 'custom') { setShowCustom(true); return; }
    setActiveFilter(id); setCustom({ start: '', end: '' });
  };
  const handleCustomApply = (s, e) => { setCustom({ start: s, end: e }); setActiveFilter('custom'); };
  const currentLabel = rangeLabel(activeFilter, custom);

  return (
    <div className="p-6 lg:p-8 w-full min-h-full">
      {/* Header */}
      <div className="mb-7">
        <h1 className="text-3xl font-black text-gray-900 dark:text-white">Expense Report</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Total spending, averages and category breakdown</p>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-2">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => handleFilter(f.id)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border-2 ${
              activeFilter === f.id
                ? 'bg-purple-600 border-purple-600 text-white shadow-lg shadow-purple-200 dark:shadow-purple-900/30'
                : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:border-purple-300'
            }`}>
            {f.id === 'custom' && activeFilter === 'custom' ? `${custom.start} → ${custom.end}` : f.label}
          </button>
        ))}
      </div>

      {currentLabel && (
        <div className="flex items-center gap-2 mb-6">
          <Calendar size={13} className="text-purple-500"/>
          <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">
            Showing: {currentLabel}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Loader2 size={36} className="animate-spin text-purple-600"/>
          <p className="text-gray-400 font-medium">Loading expenses…</p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard title="Total Expenses" value={fmt(total)}           sub={`${count} transactions`}          gradient="bg-gradient-to-br from-red-500 to-rose-600"     icon={TrendingDown}/>
            <StatCard title="Average Expense" value={fmt(Math.round(avg))} sub="Per transaction"                  gradient="bg-gradient-to-br from-orange-500 to-amber-600"  icon={BarChart3}/>
            <StatCard title="Top Category"    value={categories[0]?.cat || '—'} sub={categories[0] ? fmt(categories[0].total) : ''} gradient="bg-gradient-to-br from-purple-600 to-violet-700" icon={Tag}/>
          </div>

          {/* Category breakdown */}
          {categories.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-6">
              <h2 className="font-bold text-gray-800 dark:text-white mb-5 flex items-center gap-2">
                <Tag size={18} className="text-purple-500"/> Spending by Category
              </h2>
              <div className="space-y-3">
                {categories.map(({ cat, total: catTotal, count: catCount }) => {
                  const barColor = CAT_COLORS[cat] || 'bg-gray-400';
                  const lightColor = CAT_LIGHT[cat] || CAT_LIGHT.Other;
                  return (
                    <div key={cat} className="flex items-center gap-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-xl flex-shrink-0 ${lightColor}`}>{cat}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-400">{catCount} transaction{catCount > 1 ? 's' : ''}</span>
                          <span className="text-sm font-black text-gray-800 dark:text-white">{fmt(catTotal)}</span>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className={`h-full ${barColor} rounded-full transition-all duration-500`}
                            style={{ width: `${(catTotal / total) * 100}%` }}/>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 w-10 text-right flex-shrink-0">
                        {((catTotal / total) * 100).toFixed(0)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent transactions */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 flex items-center gap-2">
              <ArrowDownCircle size={18} className="text-red-400"/>
              <h2 className="font-bold text-gray-800 dark:text-white">Recent Transactions</h2>
              <span className="ml-auto text-xs text-gray-400">Last {recentExpenses.length}</span>
            </div>
            {recentExpenses.length === 0 ? (
              <div className="text-center py-12">
                <Wallet size={28} className="mx-auto text-gray-200 dark:text-slate-600 mb-2"/>
                <p className="text-sm text-gray-400">No expenses for this period</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-slate-700">
                {recentExpenses.map((exp, i) => {
                  const lightColor = CAT_LIGHT[exp.category] || CAT_LIGHT.Other;
                  return (
                    <div key={exp.id || i} className="flex items-center gap-4 px-6 py-3.5 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{exp.title || 'Expense'}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg ${lightColor}`}>{exp.category || 'Other'}</span>
                          <span className="text-xs text-gray-400">{fmtDate(exp.expense_date || exp.created_at)}</span>
                          {exp.paid_to && <span className="text-xs text-gray-400">· {exp.paid_to}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-base font-black text-red-500">−{fmt(exp.amount)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Empty state */}
          {count === 0 && (
            <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700">
              <Wallet size={36} className="mx-auto text-gray-200 dark:text-slate-600 mb-3"/>
              <h3 className="text-lg font-black text-gray-400">No expenses found</h3>
              <p className="text-sm text-gray-400 mt-1">Try a different time period</p>
            </div>
          )}
        </div>
      )}
      <CustomDateModal open={showCustom} onClose={() => setShowCustom(false)} onApply={handleCustomApply}/>
    </div>
  );
}